import { useState } from "react";
import { MapPin, Phone, Mail, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PublicPage } from "@/components/landing/PublicPage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/portal/JamaahFields";

const INITIAL = { name: "", phone: "", email: "", subject: "", message: "" };
const MAPS_URL = "https://maps.app.goo.gl/w2YZf7P37et7EF5v5";

export default function Kontak() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const q = encodeURIComponent("Masjid Al-Iman Banjarwaru Kawali Ciamis");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/messages", form);
      setDone(true);
      setForm(INITIAL);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicPage testId="kontak-page" eyebrow="Hubungi Kami" title="Kontak Masjid Al-Iman" desc="Silakan hubungi pengurus untuk informasi kegiatan, donasi, layanan kemasjidan, atau kirim pesan dan pertanyaan melalui form di bawah.">
      {(profile) => (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 space-y-4 text-sm" data-testid="kontak-info">
              <p className="flex gap-3"><MapPin size={18} className="shrink-0 text-emerald-800" /><span>{profile?.address}</span></p>
              {profile?.phone && <p className="flex gap-3"><Phone size={18} className="shrink-0 text-emerald-800" /><a href={`tel:${profile.phone}`} className="hover:underline" data-testid="kontak-phone">{profile.phone}</a></p>}
              {profile?.email && <p className="flex gap-3"><Mail size={18} className="shrink-0 text-emerald-800" /><a href={`mailto:${profile.email}`} className="hover:underline" data-testid="kontak-email">{profile.email}</a></p>}
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full emerald-bg text-white px-4 py-2 text-xs font-semibold hover:bg-[#145c48]" data-testid="kontak-maps-link"><MapPin size={14} /> Buka di Google Maps</a>
            </div>
            <div className="rounded-3xl overflow-hidden border border-emerald-900/10 min-h-[300px] bg-emerald-50">
              <iframe title="Peta Masjid Al-Iman" src={`https://maps.google.com/maps?q=${q}&z=15&output=embed`} className="w-full h-full min-h-[300px]" loading="lazy" data-testid="kontak-map" />
            </div>
          </div>
          <div className="lg:col-span-3 rounded-3xl bg-white border border-emerald-900/10 p-6 sm:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="kontak-success">
                <CheckCircle2 size={48} className="mx-auto text-emerald-700" />
                <h3 className="mt-4 text-xl font-bold text-emerald-950">Pesan Terkirim</h3>
                <p className="mt-2 text-sm text-slate-600">Terima kasih. Pengurus akan menindaklanjuti pesan Anda secepatnya.</p>
                <Button variant="outline" className="mt-6 rounded-full" onClick={() => setDone(false)} data-testid="kontak-again-btn">Kirim pesan lain</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="kontak-form">
                <h2 className="text-xl font-bold text-emerald-950">Kirim Pesan / Pertanyaan</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field id="k-name" label="Nama" required><Input id="k-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="kontak-name-input" /></Field>
                  <Field id="k-phone" label="No. WhatsApp"><Input id="k-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="08xxxxxxxxxx" data-testid="kontak-phone-input" /></Field>
                  <Field id="k-email" label="Email"><Input id="k-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="kontak-email-input" /></Field>
                  <Field id="k-subject" label="Judul"><Input id="k-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Pertanyaan tentang..." data-testid="kontak-subject-input" /></Field>
                </div>
                <Field id="k-message" label="Pesan" required><Textarea id="k-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} required minLength={3} data-testid="kontak-message-input" /></Field>
                <Button type="submit" className="rounded-full h-11 px-6 emerald-bg hover:bg-[#145c48]" disabled={loading} data-testid="kontak-submit-btn">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <><Send size={15} /> Kirim Pesan</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </PublicPage>
  );
}
